import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation } from '@/hoslog-intl';
import { EditIcon, GalleryIcon, LockCloseIcon, LockOpenIcon, TrashIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { HostListItem, ToggleHostStatusPayload } from '@/services/hostManagement/hostManagement.types';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from 'ui/button';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

interface GetColumnsProps {
  openConfirmation: OpenConfirmationProps;
  t: (key: string, options?: Record<string, string>) => string;
  mutate: (payload: ToggleHostStatusPayload) => Promise<unknown>;
}

export function getColumns({ openConfirmation, mutate, t }: GetColumnsProps): ColumnDef<HostListItem>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem] text-caption text-primary">
          {getSerialNumber(table, row.index)}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'position',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.form.basicInfo.position')} />
      ),
      cell: ({ row }) => {
        return (
          <div className="font-medium truncate max-w-[20rem] text-caption text-primary">{row.getValue('position')}</div>
        );
      },
      meta: {
        label: 'Position',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'hostNameEng',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.page.table.hostName')} />
      ),
      cell: ({ row }) => <div className="text-caption font-medium text-primary">{row.getValue('hostNameEng')}</div>,
      meta: {
        label: 'Host Name',
      },
      enableHiding: false,
    },
    {
      accessorKey: 'rank',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('storeManagement.hostManagement.form.basicInfo.rank')} />
      ),
      cell: ({ row }) => (
        <div className="text-caption font-medium text-primary">
          {row.getValue('rank') !== 0 && Number(row.getValue('rank')) <= 10 ? (
            row.getValue('rank')
          ) : (
            <p>
              <Translation id="storeManagement.hostManagement.page.table.unranked" />
            </p>
          )}
        </div>
      ),
      meta: {
        label: 'Rank',
      },
      enableHiding: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => {
        return (
          <div className="font-medium">
            {row.getValue('status') === 'A' ? (
              <div className="text-success text-caption font-medium">{t('common.page.table.active')}</div>
            ) : (
              <div className="text-destructive-text text-caption font-medium">{t('common.page.table.blocked')}</div>
            )}
          </div>
        );
      },
      meta: {
        label: 'Status',
        variant: 'multiSelect',
      },
      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const date = Number(row.getValue<Date>('createdDate'));
        return (
          <div className="font-medium whitespace-nowrap text-caption text-primary">
            {date ? formatDate(date, 'YYYY/MM/DD HH:mm:ss') : '-'}
          </div>
        );
      },
      meta: {
        label: 'Created Date',
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        const date = Number(row.getValue('updatedDate'));

        return (
          <div className="font-medium whitespace-nowrap text-caption text-primary">
            {date ? formatDate(date, 'YYYY/MM/DD HH:mm:ss') : '-'}
          </div>
        );
      },
      meta: {
        label: 'Updated Date',
      },
      enableHiding: false,
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.actions')} />,
      meta: {
        label: 'Action',
      },
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center space-x-2 ">
          {row.original.status === 'A' && (
            <>
              <Can rule={{ allOf: [PERMS.host.detailPremiumView] }}>
                <Link
                  to="/store-information/premium-stores/host-management/$clubid/$manage-host"
                  params={{ clubid: row.original.clubId, 'manage-host': row.original.hostCode }}
                >
                  <EditIcon className="text-foreground-subdued cursor-pointer h-4 w-4" />
                </Link>
              </Can>
              <Can rule={{ allOf: [PERMS.gallery.premium.list] }}>
                <Link
                  to="/store-information/premium-stores/host-management/$clubid/gallery/$host-gallery"
                  params={{ clubid: row.original.clubId, 'host-gallery': row.original.hostId }}
                  search={(search) => search}
                >
                  <GalleryIcon className="text-warning cursor-pointer h-4 w-4" />
                </Link>
              </Can>
            </>
          )}
          <Can rule={{ allOf: [PERMS.host.premium.block] }}>
            <Button
              variant={'ghost'}
              className="p-0 m-0"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description:
                    row.getValue('status') === 'A'
                      ? t('common.page.modal.blockMessage')
                      : t('common.page.modal.unblockMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.confirm'),
                  onAction: async () => {
                    await mutate({
                      clubId: row.original.clubId,
                      hostId: row.original.hostId,
                      status: row.getValue('status') === 'A' ? 'B' : 'A',
                    });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === 'A' ? (
                <LockCloseIcon className="text-destructive-text size-4" />
              ) : (
                <LockOpenIcon className="text-link size-4" />
              )}
            </Button>
          </Can>
          <Can rule={{ allOf: [PERMS.host.premium.block] }}>
            {row.original.status === 'A' && (
              <TrashIcon
                size={16}
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirmation.title'),
                    description: t('common.page.modal.deleteStaffMessage', { staffName: row.original.hostNameEng }),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.confirm'),
                    onAction: async () => {
                      await mutate({
                        clubId: row.original.clubId,
                        hostId: row.original.hostId,
                        status: 'D',
                      });
                    },
                    onCancel: () => {},
                    size: 'lg',
                    variant: 'destructive',
                  });
                }}
                className="cursor-pointer text-destructive-text hover:text-destructive-text ml-2"
              />
            )}
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
